import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './Modal.css';

interface EditManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  managerId: number;
}

interface ManagerFormData {
  manager_name: string;
  email: string;
  role: 'regional' | 'district';
  reports_to?: string;
  manages?: string[];
}

const EditManagerModal: React.FC<EditManagerModalProps> = ({ isOpen, onClose, onSuccess, managerId }) => {
  const [formData, setFormData] = useState<ManagerFormData>({
    manager_name: '',
    email: '',
    role: 'district',
    reports_to: '',
    manages: [],
  });

  const [originalData, setOriginalData] = useState<ManagerFormData | null>(null);
  const [regionalManagers, setRegionalManagers] = useState<string[]>([]);
  const [districtManagers, setDistrictManagers] = useState<string[]>([]);
  const [dealers, setDealers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      fetchManagerData();
    }
  }, [isOpen, managerId]);

  const fetchManagerData = async () => {
    setLoading(true);
    try {
      // Fetch manager details
      console.log('[EditManagerModal] Fetching manager with ID:', managerId);
      const managerResponse = await adminAPI.getManager(managerId);
      console.log('[EditManagerModal] API Response:', managerResponse);

      // Access data from Axios response wrapper
      const responseData = managerResponse.data || managerResponse;
      console.log('[EditManagerModal] Response data:', responseData);

      if (responseData.success) {
        const manager = responseData.manager;
        console.log('[EditManagerModal] Manager data:', manager);

        const initialData: ManagerFormData = {
          manager_name: manager.manager_name || '',
          email: manager.email || '',
          role: manager.role || 'district',
          reports_to: manager.reports_to || '',
          manages: manager.manages || [],
        };
        console.log('[EditManagerModal] Initial form data:', initialData);

        setFormData(initialData);
        setOriginalData(initialData);
        console.log('[EditManagerModal] Form data set successfully');
      } else {
        console.error('[EditManagerModal] Response success=false:', responseData);
      }

      // Fetch regional managers for "reports to" field
      const regionalResponse = await adminAPI.listManagers({ role: 'regional', per_page: 1000 });
      const regionalData = regionalResponse.data || regionalResponse;
      if (regionalData.success) {
        setRegionalManagers(regionalData.managers.map((m: any) => m.manager_name));
      }

      // Fetch district managers (for reassigning entities if role changes)
      const districtResponse = await adminAPI.listManagers({ role: 'district', per_page: 1000 });
      const districtData = districtResponse.data || districtResponse;
      if (districtData.success) {
        setDistrictManagers(districtData.managers.map((m: any) => m.manager_name));
      }

      // Fetch dealers (for assigning/reassigning)
      const dealersResponse = await adminAPI.getAllUsers({ role: 'dealer', per_page: 1000 });
      const dealersData = dealersResponse.data || dealersResponse;
      if (dealersData.success) {
        setDealers(dealersData.users);
      }
    } catch (err) {
      console.error('Error fetching manager data:', err);
      setErrors({ submit: 'Failed to load manager data' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  const handleManagesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = e.target.options;
    const selected: string[] = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setFormData((prev) => ({ ...prev, manages: selected }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.manager_name.trim()) {
      newErrors.manager_name = 'Manager name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Regional managers shouldn't have "reports to"
    if (formData.role === 'regional' && formData.reports_to) {
      newErrors.reports_to = 'Regional managers do not report to anyone in this system';
    }

    // District managers must report to a regional manager
    if (formData.role === 'district' && !formData.reports_to) {
      newErrors.reports_to = 'District managers must report to a regional manager';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData: any = {
        manager_name: formData.manager_name,
        email: formData.email,
        role: formData.role,
      };

      // Only include reports_to for district managers
      if (formData.role === 'district' && formData.reports_to) {
        submitData.reports_to = formData.reports_to;
      }

      // Include manages array
      if (formData.manages && formData.manages.length > 0) {
        submitData.manages = formData.manages;
      }

      const response = await adminAPI.updateManager(managerId, submitData);

      if (response.success) {
        alert('Manager updated successfully!');
        onSuccess();
        handleClose();
      } else {
        setErrors({ submit: response.error || 'Failed to update manager' });
      }
    } catch (err: any) {
      console.error('Error updating manager:', err);
      setErrors({
        submit: err.response?.data?.error || err.message || 'Failed to update manager',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      manager_name: '',
      email: '',
      role: 'district',
      reports_to: '',
      manages: [],
    });
    setOriginalData(null);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const getAvailableEntities = () => {
    if (formData.role === 'regional') {
      // Regional managers manage district managers (filter out empty names)
      return districtManagers
        .filter((dm) => dm && dm.trim())
        .map((dm) => ({ value: dm, label: dm }));
    } else {
      // District managers manage dealers (filter out entries with missing data)
      return dealers
        .filter((d) => d.dealer_id && d.dealer_name)
        .map((d) => ({
          value: d.dealer_id,
          label: `${d.dealer_name} (${d.dealer_id})`
        }));
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="material-icons">edit</span>
            Edit Manager
          </h2>
          <button className="modal-close" onClick={handleClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        {loading ? (
          <div className="modal-body">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="loading-spinner"></div>
              <p>Loading manager details...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="modal-body">
              {errors.submit && (
                <div className="form-error-banner">
                  <span className="material-icons">error</span>
                  {errors.submit}
                </div>
              )}

              {/* Manager Information */}
              <div className="form-section">
                <h3>Manager Information</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="manager_name">
                      Manager Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="manager_name"
                      name="manager_name"
                      value={formData.manager_name}
                      onChange={handleInputChange}
                      className={errors.manager_name ? 'error' : ''}
                      placeholder="e.g., John Smith"
                    />
                    {errors.manager_name && <span className="field-error">{errors.manager_name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? 'error' : ''}
                      placeholder="john.smith@company.com"
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                </div>
              </div>

              {/* Role and Hierarchy */}
              <div className="form-section">
                <h3>Role and Hierarchy</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="role">
                      Role <span className="required">*</span>
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className={errors.role ? 'error' : ''}
                    >
                      <option value="district">District Manager</option>
                      <option value="regional">Regional Manager</option>
                    </select>
                    {errors.role && <span className="field-error">{errors.role}</span>}
                  </div>

                  {formData.role === 'district' && (
                    <div className="form-group">
                      <label htmlFor="reports_to">
                        Reports To <span className="required">*</span>
                      </label>
                      <select
                        id="reports_to"
                        name="reports_to"
                        value={formData.reports_to}
                        onChange={handleInputChange}
                        className={errors.reports_to ? 'error' : ''}
                      >
                        <option value="">Select Regional Manager</option>
                        {regionalManagers.map((rm) => (
                          <option key={rm} value={rm}>
                            {rm}
                          </option>
                        ))}
                      </select>
                      {errors.reports_to && <span className="field-error">{errors.reports_to}</span>}
                    </div>
                  )}
                </div>

                {formData.role === 'regional' && formData.reports_to && (
                  <div className="form-success-banner">
                    <span className="material-icons">info</span>
                    <div>
                      Note: Regional managers do not report to anyone in this system. The "reports to" field will be cleared.
                    </div>
                  </div>
                )}
              </div>

              {/* Managed Entities */}
              <div className="form-section">
                <h3>
                  Manages {formData.role === 'regional' ? 'District Managers' : 'Dealers'}
                </h3>

                <div className="form-group">
                  <label htmlFor="manages">
                    Select {formData.role === 'regional' ? 'District Managers' : 'Dealers'}
                  </label>
                  <div className="checkbox-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {getAvailableEntities().map((entity) => (
                      <div key={entity.value} className="checkbox-item">
                        <input
                          type="checkbox"
                          id={`entity-${entity.value}`}
                          value={entity.value}
                          checked={formData.manages?.includes(entity.value) || false}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => {
                              const manages = prev.manages || [];
                              if (e.target.checked) {
                                return { ...prev, manages: [...manages, value] };
                              } else {
                                return { ...prev, manages: manages.filter((m) => m !== value) };
                              }
                            });
                          }}
                        />
                        <label htmlFor={`entity-${entity.value}`}>{entity.label}</label>
                      </div>
                    ))}
                  </div>
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                    Currently managing: {formData.manages?.length || 0} {formData.role === 'regional' ? 'district managers' : 'dealers'}
                  </small>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting || loading}>
                {submitting ? (
                  <>
                    <span className="button-spinner"></span>
                    Updating Manager...
                  </>
                ) : (
                  <>
                    <span className="material-icons">save</span>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditManagerModal;
